import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useEffect, useRef } from 'react';
import type { LocationData } from '../services/eventService';

// Mapbox access token
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1IjoiamxhYm9yZGUiLCJhIjoiY21lN2ZjdnpkMDN6eTJscHY5MGx5ZGJnOCJ9.EA2QwsQS0qydRCBeu4vXmQ';

interface EventMapProps {
  location: LocationData;
  className?: string;
}

export function EventMap({ location, className = '' }: EventMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);

  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    // Initialize map
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11', // Light theme to match app design
      center: [location.coordinates.longitude, location.coordinates.latitude],
      zoom: 15,
      attributionControl: false
    });

    // Add custom attribution
    map.current.addControl(new mapboxgl.AttributionControl({
      compact: true
    }), 'bottom-right');

    // Add navigation controls
    map.current.addControl(new mapboxgl.NavigationControl({
      showCompass: false,
      showZoom: true
    }), 'top-right');

    // Create custom marker element
    const markerElement = document.createElement('div');
    markerElement.className = 'custom-marker';
    markerElement.style.cssText = `
      width: 32px;
      height: 32px;
      background-color: #a7727d;
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      cursor: pointer;
      position: relative;
    `;

    // Add marker
    new mapboxgl.Marker(markerElement)
      .setLngLat([location.coordinates.longitude, location.coordinates.latitude])
      .addTo(map.current);

    // Add popup with location info
    const popup = new mapboxgl.Popup({
      offset: 25,
      closeButton: false,
      closeOnClick: false,
      className: 'custom-popup'
    })
      .setLngLat([location.coordinates.longitude, location.coordinates.latitude])
      .setHTML(`
        <div style="padding: 8px; font-family: Inter, sans-serif;">
          <div style="font-weight: 600; color: #1f2937; margin-bottom: 4px;">${location.name}</div>
          <div style="font-size: 14px; color: #6b7280;">${location.address}</div>
        </div>
      `);

    // Show popup on marker hover
    markerElement.addEventListener('mouseenter', () => {
      popup.addTo(map.current!);
    });

    markerElement.addEventListener('mouseleave', () => {
      popup.remove();
    });

    // Clean up on unmount
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [location]);

  const handleGetDirections = () => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${location.coordinates.latitude},${location.coordinates.longitude}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (!mapboxgl.accessToken || mapboxgl.accessToken === 'pk.your_mapbox_token_here') {
    return (
      <div className={`bg-gray-100 rounded-2xl p-8 text-center ${className}`}>
        <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Map not available</h3>
        <p className="text-gray-600 mb-4">Mapbox access token required for map display</p>
        <button
          onClick={handleGetDirections}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          Get Directions
        </button>
      </div>
    );
  }

  return (
    <div className={`relative w-full ${className}`}>
      <div 
        ref={mapContainer} 
        className="w-full h-full rounded-xl overflow-hidden"
        style={{  maxWidth: '100%' }}
      />
      
      {/* Location Info Card - Mobile */}
      <div className="absolute top-3 left-3 right-3 md:hidden bg-white rounded-lg shadow-lg border border-gray-200 p-2 max-w-xs">
        <div className="text-sm font-medium text-gray-900 truncate">{location.name}</div>
        <div className="text-xs text-gray-600 truncate">{location.address}</div>
      </div>
      
      {/* Get Directions Button */}
      <button
        onClick={handleGetDirections}
        className="absolute bottom-3 right-3 bg-white hover:bg-gray-50 text-gray-900 px-3 py-2 rounded-lg shadow-lg border border-gray-200 transition-colors flex items-center gap-2 text-sm font-medium"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
        <span className="hidden sm:inline">Directions</span>
      </button>
    </div>
  );
}